import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Input, Button, Badge, Spinner, Alert } from 'reactstrap';
import { supabase } from '../../lib/supabase';
import { toast, Toaster } from 'react-hot-toast';
import './AdminServices.scss';

const AdminServices = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Inline editing state
    const [editingId, setEditingId] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    // Add new service state
    const [newService, setNewService] = useState({
        category: '',
        subcategory: '',
        service_name: '',
        min_price: '',
        max_price: '',
        description: '',
        display_order: 0,
        is_active: true
    });

    const categories = [
        'SEO', 'Paid Media', 'Data', 'Strategy', 'Experience',
        'Marketing', 'Analytics', 'Industry'
    ];

    // Fetch services
    const fetchServices = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('pricing_services')
                .select('*')
                .order('display_order', { ascending: true })
                .order('category', { ascending: true });

            if (error) throw error;
            setServices(data || []);
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
            toast.error('Failed to load services');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
    }, []);

    // Filter services
    const filteredServices = services.filter(service =>
        searchTerm === '' ||
        service.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.subcategory?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Start inline editing
    const startEditing = (service) => {
        setEditingId(service.id);
        setEditFormData({
            category: service.category,
            subcategory: service.subcategory,
            service_name: service.service_name,
            min_price: service.min_price,
            max_price: service.max_price,
            description: service.description,
            display_order: service.display_order,
            is_active: service.is_active
        });
    };

    // Save inline edit
    const saveEdit = async (id) => {
        try {
            // Validate
            if (!editFormData.service_name.trim() || !editFormData.min_price || !editFormData.max_price) {
                toast.error('Please fill required fields');
                return;
            }

            const { error } = await supabase
                .from('pricing_services')
                .update({
                    ...editFormData,
                    min_price: parseFloat(editFormData.min_price),
                    max_price: parseFloat(editFormData.max_price),
                    display_order: parseInt(editFormData.display_order) || 0,
                    updated_at: new Date().toISOString()
                })
                .eq('id', id);

            if (error) throw error;

            toast.success('Service updated');
            setEditingId(null);
            fetchServices();
        } catch (err) {
            toast.error('Update failed');
        }
    };

    // Cancel edit
    const cancelEdit = () => {
        setEditingId(null);
        setEditFormData({});
    };

    // Delete service
    const deleteService = async (id) => {
        if (!window.confirm('Delete this service?')) return;

        try {
            const { error } = await supabase
                .from('pricing_services')
                .delete()
                .eq('id', id);

            if (error) throw error;

            toast.success('Service deleted');
            fetchServices();
        } catch (err) {
            toast.error('Delete failed');
        }
    };

    // Toggle active status
    const toggleActive = async (service) => {
        try {
            const { error } = await supabase
                .from('pricing_services')
                .update({
                    is_active: !service.is_active,
                    updated_at: new Date().toISOString()
                })
                .eq('id', service.id);

            if (error) throw error;

            toast.success(`Service ${!service.is_active ? 'activated' : 'deactivated'}`);
            fetchServices();
        } catch (err) {
            toast.error('Update failed');
        }
    };

    // Add new service
    const addService = async () => {
        try {
            // Validate
            if (!newService.service_name.trim() || !newService.min_price || !newService.max_price) {
                toast.error('Please fill required fields');
                return;
            }

            const { error } = await supabase
                .from('pricing_services')
                .insert([{
                    category: newService.category,
                    subcategory: newService.subcategory,
                    service_name: newService.service_name,
                    min_price: parseFloat(newService.min_price),
                    max_price: parseFloat(newService.max_price),
                    description: newService.description,
                    display_order: parseInt(newService.display_order) || 0,
                    is_active: newService.is_active
                }]);

            if (error) throw error;

            toast.success('Service added');
            setNewService({
                category: '',
                subcategory: '',
                service_name: '',
                min_price: '',
                max_price: '',
                description: '',
                display_order: services.length + 1,
                is_active: true
            });
            fetchServices();
        } catch (err) {
            toast.error('Add failed');
        }
    };

    if (loading) {
        return (
            <div className="admin-loading">
                <Spinner color="primary" />
                <p>Loading services...</p>
            </div>
        );
    }

    return (
        <>
            <Toaster position="top-right" />

            <Container className="admin-services-container">
                {/* Header */}
                <div className="admin-header">
                    <h1><i className="fas fa-cogs"></i> Services Management</h1>
                    <p className="subtitle">{services.length} services total</p>
                </div>

                {/* Search and Actions */}
                <div className="admin-controls">
                    <div className="search-box">
                        <i className="fas fa-search"></i>
                        <Input
                            type="text"
                            placeholder="Search services..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <Button color="primary" onClick={fetchServices} className="refresh-btn">
                        <i className="fas fa-sync"></i> Refresh
                    </Button>
                </div>

                {/* Add New Service Form */}
                <Card className="add-service-card">
                    <h3><i className="fas fa-plus"></i> Add New Service</h3>
                    <div className="add-service-form">
                        <div className="form-row">
                            <Input
                                type="text"
                                placeholder="Service Name"
                                value={newService.service_name}
                                onChange={(e) => setNewService({ ...newService, service_name: e.target.value })}
                                className="form-input"
                            />
                            <select
                                value={newService.category}
                                onChange={(e) => setNewService({ ...newService, category: e.target.value })}
                                className="form-select"
                            >
                                <option value="">Select Category</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-row">
                            <Input
                                type="text"
                                placeholder="Subcategory (optional)"
                                value={newService.subcategory}
                                onChange={(e) => setNewService({ ...newService, subcategory: e.target.value })}
                                className="form-input"
                            />
                            <Input
                                type="number"
                                placeholder="Min Price"
                                value={newService.min_price}
                                onChange={(e) => setNewService({ ...newService, min_price: e.target.value })}
                                className="form-input"
                                step="0.01"
                            />
                            <Input
                                type="number"
                                placeholder="Max Price"
                                value={newService.max_price}
                                onChange={(e) => setNewService({ ...newService, max_price: e.target.value })}
                                className="form-input"
                                step="0.01"
                            />
                        </div>

                        <div className="form-row">
                            <Input
                                type="textarea"
                                placeholder="Description (optional)"
                                value={newService.description}
                                onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                                className="form-textarea"
                            />
                            <div className="form-actions">
                                <Input
                                    type="number"
                                    placeholder="Order"
                                    value={newService.display_order}
                                    onChange={(e) => setNewService({ ...newService, display_order: e.target.value })}
                                    className="order-input"
                                />
                                <label className="active-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={newService.is_active}
                                        onChange={(e) => setNewService({ ...newService, is_active: e.target.checked })}
                                    /> Active
                                </label>
                                <Button color="success" onClick={addService} className="add-btn">
                                    <i className="fas fa-check"></i> Add Service
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Services Table */}
                <Card className="services-table-card">
                    {error && (
                        <Alert color="danger">
                            <i className="fas fa-exclamation-triangle"></i> {error}
                        </Alert>
                    )}

                    {filteredServices.length === 0 ? (
                        <div className="empty-state">
                            <i className="fas fa-inbox"></i>
                            <p>No services found</p>
                        </div>
                    ) : (
                        <div className="services-table">
                            <div className="table-header">
                                <div className="header-cell">Service</div>
                                <div className="header-cell">Category</div>
                                <div className="header-cell">Price Range</div>
                                <div className="header-cell">Status</div>
                                <div className="header-cell">Order</div>
                                <div className="header-cell actions">Actions</div>
                            </div>

                            <div className="table-body">
                                {filteredServices.map(service => (
                                    <div key={service.id} className="table-row">
                                        {editingId === service.id ? (
                                            // Edit Mode
                                            <>
                                                <div className="table-cell">
                                                    <Input
                                                        type="text"
                                                        value={editFormData.service_name}
                                                        onChange={(e) => setEditFormData({ ...editFormData, service_name: e.target.value })}
                                                        className="edit-input"
                                                    />
                                                    <Input
                                                        type="textarea"
                                                        value={editFormData.description}
                                                        onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                                                        className="edit-textarea"
                                                        placeholder="Description"
                                                    />
                                                </div>

                                                <div className="table-cell">
                                                    <select
                                                        value={editFormData.category}
                                                        onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                                                        className="edit-select"
                                                    >
                                                        {categories.map(cat => (
                                                            <option key={cat} value={cat}>{cat}</option>
                                                        ))}
                                                    </select>
                                                    <Input
                                                        type="text"
                                                        value={editFormData.subcategory}
                                                        onChange={(e) => setEditFormData({ ...editFormData, subcategory: e.target.value })}
                                                        className="edit-input"
                                                        placeholder="Subcategory"
                                                    />
                                                </div>

                                                <div className="table-cell">
                                                    <div className="price-inputs">
                                                        <div className="price-group">
                                                            <span>Min:</span>
                                                            <Input
                                                                type="number"
                                                                value={editFormData.min_price}
                                                                onChange={(e) => setEditFormData({ ...editFormData, min_price: e.target.value })}
                                                                className="price-input"
                                                                step="0.01"
                                                            />
                                                        </div>
                                                        <div className="price-group">
                                                            <span>Max:</span>
                                                            <Input
                                                                type="number"
                                                                value={editFormData.max_price}
                                                                onChange={(e) => setEditFormData({ ...editFormData, max_price: e.target.value })}
                                                                className="price-input"
                                                                step="0.01"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="table-cell">
                                                    <label className="edit-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={editFormData.is_active}
                                                            onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                                                        /> Active
                                                    </label>
                                                </div>

                                                <div className="table-cell">
                                                    <Input
                                                        type="number"
                                                        value={editFormData.display_order}
                                                        onChange={(e) => setEditFormData({ ...editFormData, display_order: e.target.value })}
                                                        className="order-edit-input"
                                                    />
                                                </div>

                                                <div className="table-cell actions">
                                                    <Button color="success" size="sm" onClick={() => saveEdit(service.id)}>
                                                        <i className="fas fa-save"></i> Save
                                                    </Button>
                                                    <Button color="secondary" size="sm" onClick={cancelEdit}>
                                                        <i className="fas fa-times"></i> Cancel
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            // View Mode
                                            <>
                                                <div className="table-cell">
                                                    <strong>{service.service_name}</strong>
                                                    {service.description && (
                                                        <p className="service-description">{service.description}</p>
                                                    )}
                                                    {service.subcategory && (
                                                        <small className="service-subcategory">{service.subcategory}</small>
                                                    )}
                                                </div>

                                                <div className="table-cell">
                                                    <Badge className="category-badge">{service.category}</Badge>
                                                </div>

                                                <div className="table-cell">
                                                    <div className="price-range">
                                                        <span className="min-price">£{parseFloat(service.min_price).toFixed(2)}</span>
                                                        <span className="price-separator"> - </span>
                                                        <span className="max-price">£{parseFloat(service.max_price).toFixed(2)}</span>
                                                    </div>
                                                </div>

                                                <div className="table-cell">
                                                    <Badge
                                                        color={service.is_active ? "success" : "secondary"}
                                                        className="status-badge"
                                                        onClick={() => toggleActive(service)}
                                                    >
                                                        {service.is_active ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </div>

                                                <div className="table-cell">
                                                    <Badge color="light" className="order-badge">
                                                        {service.display_order}
                                                    </Badge>
                                                </div>

                                                <div className="table-cell actions">
                                                    <Button color="warning" size="sm" onClick={() => startEditing(service)}>
                                                        <i className="fas fa-edit"></i> Edit
                                                    </Button>
                                                    <Button color="danger" size="sm" onClick={() => deleteService(service.id)}>
                                                        <i className="fas fa-trash"></i>
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Card>

                {/* Stats Footer */}
                <div className="stats-footer">
                    <div className="stat-item">
                        <span className="stat-label">Total:</span>
                        <span className="stat-value">{services.length}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Active:</span>
                        <span className="stat-value">{services.filter(s => s.is_active).length}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Categories:</span>
                        <span className="stat-value">{[...new Set(services.map(s => s.category))].length}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Showing:</span>
                        <span className="stat-value">{filteredServices.length}</span>
                    </div>
                </div>
            </Container>
        </>
    );
};

export default AdminServices;